import styled from "@emotion/styled";
import { Box, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles/createTheme";
import { RESPONSIVE_DRAWER, shouldForwardProp } from "../common/Common.style";
import theme from "../theme";

// At viewport < 1280 the chat header allows a long group name to wrap onto
// multiple lines (header grows vertically) so action buttons stay visible
// At ≥ 1280 the previous single-row-with-ellipsis layout is
// preserved.
const BELOW_WIDE_BP = `@media (max-width: ${RESPONSIVE_DRAWER.WIDE_BP - 1}px)`;

type NameTextProps = {
  fontSize: number;
};

export const MessageHeaderWrapper = styled(Box)`
  height: 65px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => (theme as Theme).palette.divider};
  box-shadow: 0 2px 4px -1px rgb(0 0 0 / 20%);
  flex-shrink: 0;

  ${BELOW_WIDE_BP} {
    height: auto;
    min-height: 65px;
  }
`;

export const HeaderDescriptionContainer = styled(Box)`
  display: flex;
  align-items: center;
  min-width: 0;
`;

type HeaderTextBlockProps = { $maxWidth?: number };

export const HeaderTextWrapper = styled(Box, {
  shouldForwardProp: prop =>
    shouldForwardProp<HeaderTextBlockProps>(["$maxWidth"], prop),
})<HeaderTextBlockProps>(({ $maxWidth }) => ({
  marginLeft: "10px",
  ...($maxWidth !== undefined ? { maxWidth: $maxWidth } : {}),
  [BELOW_WIDE_BP]: {
    flex: 1,
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
}));

export const HeaderDescriptionWrapper = styled(Box)`
  display: flex;
  flex-grow: 3;
  min-width: 0;
  overflow: hidden;
  cursor: pointer;
`;

export const PrivateHeaderWrapper = styled(Box)`
  display: flex;
  flex: 1;
`;

export const BroadcastHeaderWrapper = styled(Box)`
  display: flex;
  flex: 1;
`;

export const PatientHeaderDescriptionWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const NameMetaWrapper = styled(Box)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${BELOW_WIDE_BP} {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;

export const NameText = styled(Typography, {
  shouldForwardProp: prop =>
    shouldForwardProp<NameTextProps>(["fontSize"], prop),
})<NameTextProps>(({ fontSize }) => ({
  textTransform: "capitalize",
  fontWeight: "normal",

  fontSize: fontSize ? `${fontSize}px` : "inherit",
  display: "inline",
  marginRight: "5px",
}));

export const AboutText = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100vw - 1000px);
`;

export const MetaText = styled(Box)`
  background: ${theme.palette.grey[500]};
  font-weight: 500;
  color: ${theme.palette.common.white};
  min-width: 50px;
  max-width: 325px;
  padding: 0 5px;
  font-size: 12px;
  border-radius: 3px;
  margin: 0 0 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  vertical-align: middle;
`;

export const ActionIcons = styled(Box)`
  margin-left: auto;
  align-items: center;
  display: flex;
`;
